import { assetsHttp } from '@/lib/api/assets-client';
import { i18n } from '@/shared/i18n';
import {
  KnowledgeBasesService,
  CreateKnowledgeBaseDto,
  KnowledgeBaseDto,
  UploadFilesResponseDto,
  UpdateKnowledgeBaseDto,
  EnhancedKnowledgeBaseSummaryDto,
  EnhancedRagFileMetadataDto,
} from '@inference.engine/assets-api-client/web';

type GetFilesByKnowledgeBaseIdOptions = NonNullable<
  Parameters<typeof KnowledgeBasesService.getFilesByKnowledgeBaseId>[0]
>;
type GetFilesByKnowledgeBaseIdQueryParams = NonNullable<GetFilesByKnowledgeBaseIdOptions['query']>;

export type GetFilesByKnowledgeBaseIdParams = Omit<
  GetFilesByKnowledgeBaseIdQueryParams,
  'sortOrder'
> & {
  sortOrder?: 'asc' | 'desc';
};

// `getKnowledgeBases` lifted to `@/entities/knowledge-base` so non-feature
// consumers don't cross into this feature. Re-exported here for backward compat.
export { getKnowledgeBases } from '@/entities/knowledge-base';

export const getKnowledgeBaseSummaryById = async (
  knowledgeBaseId: string,
): Promise<EnhancedKnowledgeBaseSummaryDto> => {
  try {
    const response = await KnowledgeBasesService.getKnowledgeBaseSummaryById({
      client: assetsHttp,
      path: {
        id: knowledgeBaseId,
      },
    });

    if (!response.data) {
      throw new Error('Empty response from getKnowledgeBases');
    }

    return response.data;
  } catch (error) {
    throw new Error(
      `Failed to get knowledge bases: ${error instanceof Error ? error.message : 'Unknown error'}`,
      { cause: error },
    );
  }
};

export const getFilesByKnowledgeBaseId = async (
  id: string,
  query: GetFilesByKnowledgeBaseIdParams,
): Promise<EnhancedRagFileMetadataDto[]> => {
  try {
    const response = await KnowledgeBasesService.getFilesByKnowledgeBaseId({
      client: assetsHttp,
      path: { id },
      query: {
        ...query,
        sortOrder:
          query.sortOrder?.toUpperCase() as GetFilesByKnowledgeBaseIdQueryParams['sortOrder'],
      },
    });

    if (!response.data) {
      throw new Error(i18n.t('knowledge:knowledgeBases.knowledgeBasesApi.emptyFileListResponse'));
    }

    return response.data;
  } catch (error) {
    throw new Error(
      i18n.t('knowledge:knowledgeBases.knowledgeBasesApi.failedToGetFiles', {
        reason:
          error instanceof Error
            ? error.message
            : i18n.t('knowledge:knowledgeBases.knowledgeBasesApi.unknownError'),
      }),
      { cause: error },
    );
  }
};

export const createKnowledgeBase = async (
  knowledgeBase: CreateKnowledgeBaseDto,
): Promise<KnowledgeBaseDto> => {
  try {
    const response = await KnowledgeBasesService.createKnowledgeBase({
      client: assetsHttp,
      body: knowledgeBase,
    });

    if (!response.data) {
      throw new Error('Empty response from createKnowledgeBase');
    }

    return response.data;
  } catch (error) {
    throw new Error(
      `Failed to create knowledge base: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`,
      { cause: error },
    );
  }
};

export const updateKnowledgeBase = async (
  id: string,
  data: UpdateKnowledgeBaseDto,
): Promise<KnowledgeBaseDto> => {
  try {
    const response = await KnowledgeBasesService.updateKnowledgeBase({
      client: assetsHttp,
      path: { id },
      body: data,
    });

    if (!response.data) {
      throw new Error('Empty response from updateKnowledgeBase');
    }

    return response.data;
  } catch (error) {
    throw new Error(
      `Failed to update knowledge base: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`,
      { cause: error },
    );
  }
};

export const deleteKnowledgeBase = async (id: string): Promise<void> => {
  try {
    await KnowledgeBasesService.deleteKnowledgeBase({ client: assetsHttp, path: { id } });
  } catch (error) {
    throw new Error(
      `Failed to delete knowledge base: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`,
      { cause: error },
    );
  }
};

export const uploadFiles = async (
  knowledgeBaseId: string,
  files: File[],
): Promise<UploadFilesResponseDto> => {
  try {
    // Validate inputs
    if (!knowledgeBaseId) {
      throw new Error(i18n.t('knowledge:knowledgeBases.knowledgeBasesApi.knowledgeBaseIdRequired'));
    }

    const response = await KnowledgeBasesService.uploadFiles({
      client: assetsHttp,
      path: { id: knowledgeBaseId },
      body: { files },
    });

    if (!response.data) {
      throw new Error(
        i18n.t('knowledge:knowledgeBases.knowledgeBasesApi.uploadFailedEmptyResponse'),
      );
    }

    return response.data;
  } catch (error) {
    throw new Error(
      i18n.t('knowledge:knowledgeBases.knowledgeBasesApi.failedToUploadFiles', {
        reason:
          error instanceof Error
            ? error.message
            : i18n.t('knowledge:knowledgeBases.knowledgeBasesApi.unknownError'),
      }),
      { cause: error },
    );
  }
};

export const deleteFile = async (knowledgeBaseId: string, fileId: string): Promise<void> => {
  try {
    await KnowledgeBasesService.deleteFileFromKnowledgeBase({
      client: assetsHttp,
      path: { id: knowledgeBaseId, fileId },
    });
  } catch (error) {
    throw new Error(
      i18n.t('knowledge:knowledgeBases.knowledgeBasesApi.failedToDeleteFile', {
        reason:
          error instanceof Error
            ? error.message
            : i18n.t('knowledge:knowledgeBases.knowledgeBasesApi.unknownError'),
      }),
      { cause: error },
    );
  }
};
